import { useState, useEffect } from "react";
import {
  Phone,
  Clock,
  Shield,
  Star,
  Plane,
  Briefcase,
  MapPin,
  Crown,
  Sparkles,
  Car,
  CheckCircle2,
  Award,
  Route,
  Users,
  ChevronRight,
  Menu,
  X,
  Gift,
  Quote,
  Mail,
} from "lucide-react";

// Foto enviada pelo cliente — Corolla Altis preto em ambiente executivo
const HERO_CAR = "/images/banner-elegance.png";

const FALLBACK_CAR = HERO_CAR;

const WHATSAPP_NUMBER = "5511999162921";
const WHATSAPP_MSG_DEFAULT =
  "Olá! Gostaria de solicitar um orçamento para transporte executivo com a Elegance.";
const WHATSAPP_MSG_DISCOUNT =
  "Olá! Gostaria de solicitar um orçamento para transporte executivo com a Elegance.";

const wa = (msg = WHATSAPP_MSG_DEFAULT) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const NAV_ITEMS = [
  { href: "#sobre", label: "Sobre" },
  { href: "#servicos", label: "Serviços" },
  { href: "#frota", label: "Frota" },
  { href: "#diferenciais", label: "Diferenciais" },
  { href: "#contato", label: "Contato" },
];

const YEARS = new Date().getFullYear() - 2012;

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative">
      {/* NAV */}
      <nav
        data-testid="main-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "nav-glass py-3" : "py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="logo-link">
            <div className="w-12 h-12 rounded-full border border-[#c9a961]/40 flex items-center justify-center bg-[#c9a961]/5 overflow-hidden">
              <img
                src="/images/logo-elegance.png"
                alt="Elegance"
                className="w-full h-full object-cover"
                style={{ filter: "sepia(0.6) saturate(1.6) hue-rotate(-10deg) brightness(1.05)" }}
              />
            </div>
            <div className="leading-tight">
              <div className="text-[#f5e5b8] tracking-[0.22em] text-[0.7rem] font-semibold">
                ELEGANCE
              </div>
              <div className="text-[#c9a961]/80 text-[0.6rem] tracking-[0.3em]">
                TRANSPORTE EXECUTIVO
              </div>
            </div>
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className="text-sm text-[#d7cfbe]/80 hover:text-[#c9a961] transition-colors tracking-wide"
              >
                {item.label}
              </a>
            ))}
          </div>

          <a
            href={wa()}
            target="_blank"
            rel="noreferrer"
            className="hidden lg:inline-flex btn-gold"
            data-testid="nav-whatsapp-btn"
          >
            <span>
              <i className="fa-brands fa-whatsapp text-lg"></i>
              WhatsApp
            </span>
          </a>

          <button
            onClick={() => setMenuOpen(true)}
            className="lg:hidden p-2 text-[#c9a961]"
            aria-label="Abrir menu"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-[60] bg-[#0a0a0b]/98 backdrop-blur-xl lg:hidden"
          data-testid="mobile-menu"
        >
          <div className="flex justify-end p-6">
            <button
              onClick={() => setMenuOpen(false)}
              className="p-2 text-[#c9a961]"
              aria-label="Fechar menu"
              data-testid="mobile-menu-close"
            >
              <X className="w-7 h-7" />
            </button>
          </div>
          <div className="flex flex-col items-center gap-8 mt-12">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="text-2xl text-[#e8d9a8] hover:text-[#c9a961]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {item.label}
              </a>
            ))}
            <a
              href={wa()}
              target="_blank"
              rel="noreferrer"
              className="btn-gold mt-6"
              onClick={() => setMenuOpen(false)}
            >
              <span>
                <i className="fa-brands fa-whatsapp text-lg"></i> Falar no WhatsApp
              </span>
            </a>
          </div>
        </div>
      )}

      {/* HERO */}
      <section
        id="top"
        className="relative min-h-screen flex items-center overflow-hidden grain-overlay"
        data-testid="hero-section"
      >
        {/* Background photo */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_CAR})` }}
        />
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b]/95 via-[#0a0a0b]/70 to-[#0a0a0b]/30 z-[1]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0b] via-transparent to-[#0a0a0b]/40 z-[1]" />
        <img src={HERO_CAR} alt="" className="hidden" onError={() => setImgError(true)} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-24 pb-16">
          <div className="max-w-3xl">
            <div className="anim-slide-right">
              <span className="badge-since" data-testid="badge-since-2014">
                <span className="dot"></span>
                <span>Desde 2012</span>
                <span className="text-[#c9a961]/70">·</span>
                <span>{YEARS} anos em Jundiaí</span>
              </span>
            </div>

            <h1
              className="hero-title text-white mt-8 text-[3rem] sm:text-[4rem] lg:text-[5.6rem] anim-fade-up delay-100"
              data-testid="hero-title"
            >
              Transporte Executivo
              <br />
              com <span className="accent">Elegância</span>,
              <br />
              <span className="accent">Segurança</span> e <span className="accent">Pontualidade</span>.
            </h1>

            <p
              className="mt-8 text-lg sm:text-xl text-[#d7cfbe] max-w-2xl leading-relaxed anim-fade-up delay-200"
              data-testid="hero-subtitle"
            >
              <strong className="text-[#f5e5b8] font-semibold">
                Frota executiva sempre nova.
              </strong>{" "}
              Atendimento 24 horas em Jundiaí, São Paulo, Campinas e região —
              motoristas profissionais e discrição absoluta em cada trajeto.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 anim-fade-up delay-300">
              <a
                href={wa()}
                target="_blank"
                rel="noreferrer"
                className="btn-gold pulse-gold"
                data-testid="hero-cta-primary"
              >
                <span>
                  <Gift className="w-5 h-5" />
                  Solicite seu Orçamento
                </span>
              </a>
              <a
                href="#servicos"
                className="btn-ghost"
                data-testid="hero-cta-secondary"
              >
                Ver Serviços <ChevronRight className="w-4 h-4" />
              </a>
            </div>

            {/* Stats */}
            <div className="mt-16 flex flex-wrap items-center gap-6 sm:gap-10 anim-fade-up delay-500">
              <Stat icon={<Clock />} big={`${YEARS}+`} label="Anos de mercado" />
              <div className="stat-divider hidden sm:block" />
              <Stat icon={<Car />} big="Novos" label="Veículos executivos" />
              <div className="stat-divider hidden sm:block" />
              <Stat icon={<Shield />} big="24h" label="Atendimento sem pausa" />
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <a
          href="#sobre"
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#c9a961]/70 hover:text-[#c9a961] transition-colors"
          data-testid="scroll-indicator"
        >
          <div className="flex flex-col items-center gap-2">
            <span className="text-[0.65rem] tracking-[0.3em] uppercase">Rolar</span>
            <div className="w-[1px] h-10 bg-gradient-to-b from-[#c9a961] to-transparent" />
          </div>
        </a>
      </section>

      {/* SOBRE */}
      <section
        id="sobre"
        className="py-32 relative overflow-hidden"
        data-testid="about-section"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="ornament">· Sobre nós ·</span>
              <h2 className="section-title text-white text-5xl lg:text-6xl mt-6 leading-[1.05]">
                Há <span className="text-gold-gradient">{YEARS} anos</span>
                <br />
                redefinindo o padrão
                <br />
                do transporte executivo.
              </h2>
              <p className="mt-8 text-[#d7cfbe]/90 text-lg leading-relaxed max-w-xl">
                Desde <strong className="text-[#f5e5b8]">2012</strong>, a Elegance
                Transporte Executivo conduz executivos, famílias e convidados
                especiais em Jundiaí e região com o mesmo compromisso:{" "}
                <em className="text-[#c9a961]">
                  conforto, segurança e discrição absoluta
                </em>
                .
              </p>
              <p className="mt-5 text-[#d7cfbe]/80 text-base leading-relaxed max-w-xl">
                Mais de uma década de estrada, milhares de trajetos concluídos
                no horário certo — e uma frota executiva sempre nova, renovada
                com frequência para entregar o melhor da tecnologia automotiva
                em cada viagem.
              </p>

              <div className="mt-10 grid grid-cols-2 gap-5 max-w-xl">
                <InfoBlock
                  icon={<Clock className="w-5 h-5" />}
                  title="Atendimento"
                  text="24 horas · todos os dias"
                />
                <InfoBlock
                  icon={<Phone className="w-5 h-5" />}
                  title="WhatsApp"
                  text="(11) 99916-2921"
                />
                <InfoBlock
                  icon={<Award className="w-5 h-5" />}
                  title="Experiência"
                  text={`Desde 2012 · ${YEARS}+ anos`}
                />
                <InfoBlock
                  icon={<MapPin className="w-5 h-5" />}
                  title="Base"
                  text="Jundiaí & Região"
                />
              </div>
            </div>

            {/* Milestone timeline */}
            <div className="relative">
              <div className="premium-card rounded-[28px] p-10 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#c9a961]/10 blur-3xl" />
                <Quote className="w-10 h-10 text-[#c9a961]/50 mb-6" />
                <p
                  className="text-2xl lg:text-3xl text-[#f5e5b8] leading-relaxed"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: "italic" }}
                >
                  "Cada trajeto, uma experiência de luxo. Cada cliente, uma
                  história de confiança construída desde 2012."
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <div className="w-12 h-[1px] bg-[#c9a961]" />
                  <span className="text-[#c9a961] text-sm tracking-[0.2em]">
                    ELEGANCE · ESSÊNCIA
                  </span>
                </div>

                <div className="mt-10 grid grid-cols-3 gap-4 pt-8 border-t border-[#c9a961]/15">
                  <MiniStat big="2012" label="Fundação" />
                  <MiniStat big={`${YEARS}+`} label="Anos no mercado" />
                  <MiniStat big="Novos" label="Veículos executivos" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVIÇOS */}
      <section
        id="servicos"
        className="py-32 relative bg-gradient-to-b from-[#0a0a0b] via-[#0f0d0a] to-[#0a0a0b]"
        data-testid="services-section"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="ornament">· Nossos serviços ·</span>
            <h2 className="section-title text-white text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Soluções <span className="text-gold-gradient">executivas</span>{" "}
              sob medida para cada compromisso.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
            <ServiceCard
              icon={<Briefcase />}
              title="Transporte Corporativo"
              text="Translados executivos para reuniões, eventos empresariais e deslocamentos diários com total discrição."
              testid="service-corporate"
            />
            <ServiceCard
              icon={<Plane />}
              title="Transfer Aeroportos"
              text="Pontualidade impecável para Guarulhos, Viracopos e Congonhas. Monitoramento de voos em tempo real."
              testid="service-airport"
            />
            <ServiceCard
              icon={<Route />}
              title="Viagens Intermunicipais"
              text="Trajetos confortáveis para qualquer cidade do estado e demais estados — sob consulta para destinos longos."
              testid="service-intercity"
            />
            <ServiceCard
              icon={<Crown />}
              title="Atendimento VIP"
              text="Serviço personalizado para clientes exigentes — água, bebidas e conforto premium à altura da sua exigência."
              testid="service-vip"
            />
            <ServiceCard
              icon={<Sparkles />}
              title="Eventos & Ocasiões"
              text="Casamentos, formaturas e ocasiões especiais. Deixe sua chegada marcante com a Elegance."
              testid="service-events"
            />
            <a
              href={wa()}
              target="_blank"
              rel="noreferrer"
              className="premium-card rounded-[20px] p-8 flex flex-col justify-between group"
              data-testid="service-custom"
            >
              <div>
                <div className="text-[#c9a961] text-xs tracking-[0.3em] uppercase">
                  Outro serviço?
                </div>
                <h3
                  className="mt-4 text-2xl text-[#f5e5b8] leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Solicite um orçamento personalizado via WhatsApp.
                </h3>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-[#c9a961] text-sm font-semibold group-hover:gap-4 transition-all">
                Falar agora <ChevronRight className="w-4 h-4" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* FROTA */}
      <section
        id="frota"
        className="py-32 relative overflow-hidden"
        data-testid="fleet-section"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#0f0d0a] via-[#0a0a0b] to-[#14110d]" />
        <div className="absolute top-1/2 right-0 w-[50%] h-[60%] -translate-y-1/2 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at center, rgba(201,169,97,0.08) 0%, transparent 70%)" }}
        />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="ornament">· Frota executiva ·</span>
            <h2 className="section-title text-white text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Veículos <span className="text-gold-gradient">novos</span>,
              padrão executivo em cada detalhe.
            </h2>
            <p className="mt-8 text-[#d7cfbe]/90 text-lg leading-relaxed">
              Nossa frota é composta exclusivamente por{" "}
              <strong className="text-[#f5e5b8]">veículos novos e executivos</strong>,
              renovada com frequência — como o Toyota Corolla Altis preto.
              Tecnologia embarcada de ponta, conforto silencioso e acabamento
              impecável para você viajar como merece.
            </p>
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <FleetFeature
              icon={<Car />}
              title="Veículos Novos"
              text="Frota renovada com frequência para garantir tecnologia, conforto e segurança de ponta."
            />
            <FleetFeature
              icon={<Sparkles />}
              title="Acabamento Premium"
              text="Bancos em couro, climatização dupla e tecnologia de série."
            />
            <FleetFeature
              icon={<Shield />}
              title="Segurança Total"
              text="Pacotes de assistência à condução e revisões em dia na concessionária."
            />
            <FleetFeature
              icon={<CheckCircle2 />}
              title="Higiene Impecável"
              text="Limpeza minuciosa antes de cada corrida. Ambiente sempre imaculado."
            />
          </div>
        </div>
      </section>

      {/* ÁREA DE ATENDIMENTO */}
      <section
        id="cobertura"
        className="py-24 relative bg-gradient-to-b from-[#0a0a0b] to-[#0f0d0a]"
        data-testid="coverage-section"
      >
        <div className="max-w-6xl mx-auto px-6">
          <div className="premium-card rounded-[28px] p-10 lg:p-14 relative overflow-hidden">
            <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full bg-[#c9a961]/8 blur-3xl pointer-events-none" />
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center relative">
              <div>
                <span className="ornament">· Onde atendemos ·</span>
                <h2 className="section-title text-white text-4xl lg:text-5xl mt-6 leading-[1.08]">
                  Da sua porta até <span className="text-gold-gradient">qualquer destino</span>.
                </h2>
                <p className="mt-6 text-[#d7cfbe]/85 text-lg leading-relaxed">
                  Atendemos principalmente <strong className="text-[#f5e5b8]">Jundiaí, São Paulo, Campinas e região</strong>.
                  Para viagens mais longas ou para outros estados, também
                  fazemos — basta solicitar um orçamento.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <CoverageCard
                  icon={<MapPin />}
                  title="Atendimento principal"
                  cities={["Jundiaí", "São Paulo", "Campinas", "Região"]}
                  highlight
                />
                <CoverageCard
                  icon={<Route />}
                  title="Viagens longas"
                  cities={["Outros estados", "Sob consulta", "Orçamento sob medida"]}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DIFERENCIAIS */}
      <section
        id="diferenciais"
        className="py-32 bg-gradient-to-b from-[#0a0a0b] to-[#0f0d0a]"
        data-testid="differentials-section"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <span className="ornament">· Nossos diferenciais ·</span>
            <h2 className="section-title text-white text-5xl lg:text-6xl mt-6">
              Por que a <span className="text-gold-gradient">Elegance</span>?
            </h2>
            <p className="mt-6 text-[#d7cfbe]/80 text-lg">
              Cinco pilares que definem a forma como conduzimos cada atendimento
              — e cada trajeto.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5 mt-16">
            <Differential
              icon={<Clock />}
              title="Atendimento 24h"
              text="Disponibilidade total — dia, noite, madrugada, finais de semana e feriados."
            />
            <Differential
              icon={<Users />}
              title="Motoristas Profissionais"
              text="Condutores treinados, discretos e com experiência em atendimento executivo."
            />
            <Differential
              icon={<Car />}
              title="Frota Nova Executiva"
              text="Veículos novos e executivos, padrão impecável de conservação e conforto."
            />
            <Differential
              icon={<CheckCircle2 />}
              title="Pontualidade Garantida"
              text="Chegamos antes. Tempo é ativo precioso — e nós respeitamos o seu."
            />
            <Differential
              icon={<Shield />}
              title="Segurança & Discrição"
              text="Rotas monitoradas, protocolos rigorosos e sigilo absoluto com cada cliente."
            />
          </div>
        </div>
      </section>

      {/* PRIMEIRA CORRIDA — CTA */}
      <section className="py-24 relative" data-testid="first-ride-cta">
        <div className="max-w-6xl mx-auto px-6">
          <div className="discount-ribbon p-10 lg:p-14 relative">
            <div className="grid lg:grid-cols-[1fr_auto] gap-10 items-center relative z-[2]">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#c9a961]/15 border border-[#c9a961]/40 text-[#f5e5b8] text-xs tracking-[0.25em] uppercase">
                  <Gift className="w-4 h-4" /> Exclusivo novos clientes
                </div>
                <h3
                  className="section-title text-white text-4xl lg:text-5xl mt-6 leading-[1.08]"
                >
                  Sua <span className="text-gold-gradient">primeira viagem</span>
                  <br />
                  com <em>desconto especial</em>.
                </h3>
                <p className="mt-5 text-[#d7cfbe]/85 text-lg max-w-2xl">
                  Experimente o padrão Elegance — frota executiva sempre nova,
                  pontualidade impecável e atendimento 24h. Fale conosco e
                  receba seu orçamento personalizado em minutos.
                </p>
              </div>
              <a
                href={wa()}
                target="_blank"
                rel="noreferrer"
                className="btn-gold shimmer text-base whitespace-nowrap"
                data-testid="first-ride-btn"
              >
                <span style={{ color: "#0a0a0b" }}>
                  <i className="fa-brands fa-whatsapp text-xl"></i>
                  Solicitar Orçamento
                </span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONTATO */}
      <section
        id="contato"
        className="py-32 relative overflow-hidden bg-gradient-to-b from-[#0a0a0b] to-[#060605]"
        data-testid="contact-section"
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#c9a961]/5 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-6 text-center">
          <span className="ornament">· Contato ·</span>
          <h2 className="section-title text-white text-5xl lg:text-7xl mt-6 leading-[1.05]">
            Pronto para sua <em className="text-gold-gradient" style={{ fontStyle: "italic" }}>próxima viagem</em>?
          </h2>
          <p className="mt-8 text-[#d7cfbe]/85 text-lg max-w-2xl mx-auto">
            Fale conosco pelo WhatsApp — retorno rápido e atendimento personalizado. Atendemos Jundiaí, São Paulo, Campinas e região; viagens
            longas e para outros estados também são feitas sob consulta.
          </p>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={wa()}
              target="_blank"
              rel="noreferrer"
              className="btn-gold pulse-gold"
              data-testid="contact-whatsapp-btn"
            >
              <span>
                <i className="fa-brands fa-whatsapp text-xl"></i>
                WhatsApp · (11) 99916-2921
              </span>
            </a>
            <a
              href="tel:+5511999162921"
              className="btn-ghost"
              data-testid="contact-phone-btn"
            >
              <Phone className="w-4 h-4" /> Ligar agora
            </a>
            <a
              href="mailto:contato@elegancetransporte.com"
              className="btn-ghost"
              data-testid="contact-email-btn"
            >
              <Mail className="w-4 h-4" /> Enviar e-mail
            </a>
          </div>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
           <a
  href="tel:+5511999162921"
  className="block h-full"
  data-testid="contact-phone-card"
>
  <div className="premium-card rounded-[18px] p-6 h-full hover:border-[#c9a961]/60 transition-colors">
    <div className="w-11 h-11 rounded-full border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] mx-auto mb-4">
      <Phone />
    </div>
    <div className="text-[#c9a961] text-[0.65rem] tracking-[0.25em] uppercase font-semibold">
      Telefone
    </div>
    <div className="text-[#f5e5b8] text-base mt-2">
      (11) 99916-2921
    </div>
  </div>
</a>
            <a
              href="mailto:contato@elegancetransporte.com"
              className="block"
              data-testid="contact-email-card"
            >
              <div className="premium-card rounded-[18px] p-6 hover:border-[#c9a961]/60 transition-colors">
                <div className="w-11 h-11 rounded-full border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] mx-auto mb-4 bg-[#c9a961]/8">
                  <Mail />
                </div>
                <div className="text-[#c9a961] text-[0.65rem] tracking-[0.25em] uppercase font-semibold">
                  E-mail
                </div>
                <div className="text-[#f5e5b8] text-sm mt-2 break-all">
                  contato@elegancetransporte.com
                </div>
              </div>
            </a>
            <ContactCard
              icon={<Clock />}
              title="Horário"
              value="24h · todos os dias"
            />
            <ContactCard
              icon={<MapPin />}
              title="Base"
              value="Jundiaí · SP"
            />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 border-t border-[#c9a961]/10" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full border border-[#c9a961]/40 flex items-center justify-center overflow-hidden">
              <img
                src="/images/logo-elegance.png"
                alt="Elegance"
                className="w-full h-full object-cover"
                style={{ filter: "sepia(0.6) saturate(1.6) hue-rotate(-10deg) brightness(1.05)" }}
              />
            </div>
            <div className="leading-tight">
              <div className="text-[#f5e5b8] tracking-[0.22em] text-[0.7rem] font-semibold">
                ELEGANCE
              </div>
              <div className="text-[#c9a961]/70 text-[0.58rem] tracking-[0.3em]">
                DESDE 2012 · JUNDIAÍ
              </div>
            </div>
          </div>
          <div className="text-[#d7cfbe]/50 text-xs text-center">
            © {new Date().getFullYear()} Elegance Transporte Executivo.
            Todos os direitos reservados.
          </div>
          <div className="text-[#c9a961]/70 text-xs tracking-[0.2em]">
            elegancetransportes.com
          </div>
        </div>
      </footer>

      {/* WHATSAPP FLOAT */}
      <a
        href={wa()}
        target="_blank"
        rel="noreferrer"
        className="wa-float fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center text-white hover:scale-110 transition-transform"
        aria-label="Falar no WhatsApp"
        data-testid="whatsapp-float-btn"
      >
        <i className="fa-brands fa-whatsapp text-3xl"></i>
      </a>
    </div>
  );
}

/* === SUBCOMPONENTES === */

function Stat({ icon, big, label }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-full border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961]">
        {icon}
      </div>
      <div>
        <div
          className="text-2xl text-[#f5e5b8] font-semibold leading-none"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {big}
        </div>
        <div className="text-[#d7cfbe]/70 text-xs tracking-wide mt-1">{label}</div>
      </div>
    </div>
  );
}

function InfoBlock({ icon, title, text }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl bg-[#c9a961]/5 border border-[#c9a961]/15">
      <div className="text-[#c9a961] mt-1">{icon}</div>
      <div>
        <div className="text-[#c9a961] text-[0.65rem] tracking-[0.25em] uppercase font-semibold">
          {title}
        </div>
        <div className="text-[#f5e5b8] text-sm mt-1">{text}</div>
      </div>
    </div>
  );
}

function MiniStat({ big, label }) {
  return (
    <div className="text-center">
      <div
        className="text-3xl text-gold-gradient font-semibold"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {big}
      </div>
      <div className="text-[#d7cfbe]/70 text-[0.65rem] tracking-[0.2em] uppercase mt-1">
        {label}
      </div>
    </div>
  );
}

function ServiceCard({ icon, title, text, testid }) {
  return (
    <div className="premium-card rounded-[20px] p-8" data-testid={testid}>
      <div className="w-14 h-14 rounded-full border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] mb-6 bg-[#c9a961]/5">
        {icon}
      </div>
      <h3
        className="text-2xl text-[#f5e5b8] mb-3"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {title}
      </h3>
      <p className="text-[#d7cfbe]/75 text-sm leading-relaxed">{text}</p>
    </div>
  );
}

function FleetFeature({ icon, title, text }) {
  return (
    <div className="premium-card rounded-[18px] p-6">
      <div className="text-[#c9a961] mb-4">{icon}</div>
      <div className="text-[#f5e5b8] font-semibold text-base mb-2">{title}</div>
      <div className="text-[#d7cfbe]/70 text-sm leading-relaxed">{text}</div>
    </div>
  );
}

function Differential({ icon, title, text }) {
  return (
    <div className="text-center p-6">
      <div className="w-16 h-16 rounded-full border border-[#c9a961]/30 mx-auto flex items-center justify-center text-[#c9a961] bg-[#c9a961]/5 mb-5">
        {icon}
      </div>
      <h4
        className="text-[#f5e5b8] text-xl mb-2"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        {title}
      </h4>
      <p className="text-[#d7cfbe]/70 text-sm leading-relaxed">{text}</p>
    </div>
  );
}

function Review({ name, avatar, title, text }) {
  return (
    <div className="premium-card rounded-[20px] p-8">
      <div className="flex gap-1 text-[#c9a961] mb-4">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-current" />
        ))}
      </div>
      <h4
        className="text-[#f5e5b8] text-xl mb-3"
        style={{ fontFamily: "'Cormorant Garamond', serif" }}
      >
        "{title}"
      </h4>
      <p className="text-[#d7cfbe]/80 text-sm leading-relaxed mb-6">{text}</p>
      <div className="flex items-center gap-3 pt-5 border-t border-[#c9a961]/10">
        <img
          src={avatar}
          alt={name}
          className="w-11 h-11 rounded-full object-cover border border-[#c9a961]/30"
        />
        <div>
          <div className="text-[#f5e5b8] text-sm font-semibold">{name}</div>
          <div className="text-[#c9a961]/70 text-xs">Cliente Elegance</div>
        </div>
      </div>
    </div>
  );
}

function ContactCard({ icon, title, value }) {
  return (
    <div className="premium-card rounded-[18px] p-6">
      <div className="w-11 h-11 rounded-full border border-[#c9a961]/30 flex items-center justify-center text-[#c9a961] mx-auto mb-4">
        {icon}
      </div>
      <div className="text-[#c9a961] text-[0.65rem] tracking-[0.25em] uppercase font-semibold">
        {title}
      </div>
      <div className="text-[#f5e5b8] text-base mt-2">{value}</div>
    </div>
  );
}

function CoverageCard({ icon, title, cities, highlight = false }) {
  return (
    <div
      className={`rounded-[18px] p-6 border backdrop-blur-md ${
        highlight
          ? "bg-[#c9a961]/10 border-[#c9a961]/45"
          : "bg-[#c9a961]/[0.03] border-[#c9a961]/15"
      }`}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full border border-[#c9a961]/40 flex items-center justify-center text-[#c9a961]">
          {icon}
        </div>
        <div className="text-[#c9a961] text-[0.65rem] tracking-[0.25em] uppercase font-semibold">
          {title}
        </div>
      </div>
      <ul className="space-y-2">
        {cities.map((c) => (
          <li
            key={c}
            className="flex items-center gap-2 text-[#f5e5b8] text-base"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            <CheckCircle2 className="w-4 h-4 text-[#c9a961] flex-shrink-0" />
            {c}
          </li>
        ))}
      </ul>
    </div>
  );
}
